import type { RGBGroup } from './color'
import type { BaseLightingTarget } from './lighting'

/** 底座驱动名称，对应 manifest.driver，必须和 DeviceFactory 注册名保持一致。 */
export const BASE_DRIVER_NAMES = ['Telink_Base', 'VT_nrf54L_Base'] as const
export type BaseDriverName = (typeof BASE_DRIVER_NAMES)[number]

/** 判断 manifest driver 是否属于底座设备。 */
export function isBaseDriverName(driver: string | undefined): driver is BaseDriverName {
  return driver === 'Telink_Base' || driver === 'VT_nrf54L_Base'
}

/** 当前设备本身或其展示树父设备属于底座时，视为底座上下文。 */
export function isBaseDockContext(device: {
  manifest: { driver: string }
  parentDevice?: { manifest: { driver: string } }
}): boolean {
  return (
    isBaseDriverName(device.manifest.driver) ||
    isBaseDriverName(device.parentDevice?.manifest.driver)
  )
}

/**
 * @deprecated 底座灯区统一使用 BaseLightingTarget。
 *
 * 历史上底座协议方法使用 light-band/light-screen，manifest 使用 base-light-band/base-light-screen。
 * 现在统一使用 manifest 灯区命名，避免同一灯区出现两套目标值。
 */
export type BaseLightTarget = BaseLightingTarget

/** 读取单个灯效配置后的结构化结果。 */
export interface BaseLightEffect {
  /** 灯效 ID。 */
  mode: number
  /** 亮度，固件使用 0-100。 */
  brightness: number
  /** 速度/频率，固件使用 0-100。 */
  frequency: number
  /** RGB 颜色，按 [R, G, B] 顺序。 */
  color: [number, number, number]
  /** 渐变方向，1 表示普通颜色，3 表示左右渐变。 */
  gradientDirection?: number
  /** 渐变颜色组，仅渐变灯效返回。 */
  rgbGroup?: RGBGroup
  /** 原始 feature report，便于调试和协议对照。 */
  raw: Uint8Array
}

/** 当前正在生效的底座灯效状态。 */
export interface BaseCurrentLightEffect {
  /** 当前灯效 ID。 */
  modeId: number
  /** 充电状态字节，保持固件原始值。P5/P6 没有充电状态灯，不使用该字段区分灯效。 */
  chargeState: number
  /** 充电时灯效显示策略。不支持充电状态灯时为 null，固件返回未知值时也为 null。 */
  chargeLightDisplayMode: BaseChargeLightDisplayMode | null
  /** 总灯效开关状态。 */
  isOn: boolean
  /** 原始 feature report，便于调试和协议对照。 */
  raw: Uint8Array
}

/** 充电时灯效显示策略。 */
export const BASE_CHARGE_LIGHT_DISPLAY_MODE = {
  /** 充电时仍显示用户选择的氛围灯效。 */
  Ambient: 'ambient',
  /** 充电时显示固件内置充电灯效。 */
  Charging: 'charging',
} as const

export type BaseChargeLightDisplayMode =
  (typeof BASE_CHARGE_LIGHT_DISPLAY_MODE)[keyof typeof BASE_CHARGE_LIGHT_DISPLAY_MODE]

/** 左右渐变灯效中某个灯珠端点的颜色配置。 */
export interface BaseGradientLightEffect {
  /** 渐变灯效 ID，1.0 中通常为 6。 */
  mode: number
  /** 灯珠索引，1.0 常用 0 和 15 表示左右端点。 */
  ledIndex: number
  /** RGB 颜色，按 [R, G, B] 顺序。 */
  color: [number, number, number]
  /** 当前端点亮度。 */
  brightness: number
  /** 原始 feature report，便于调试和协议对照。 */
  raw: Uint8Array
}

/** 屏幕素材上传选项。 */
export interface BaseScreenUploadOptions {
  /** 上传进度回调，范围 0-100。 */
  onProgress?: (progress: number) => void
}

/** 屏幕播放顺序配置。 */
export interface BaseImageOrder {
  /** 实际参与轮播的页面数量。 */
  imageNum: number
  /** 固件中的页面顺序数组，长度通常为 8。 */
  order: number[]
  /** 原始 feature report，便于调试和协议对照。 */
  raw: Uint8Array
}

/** 底座屏显页面列表项，列表长度由 manifest 决定，数组顺序即播放顺序。 */
export interface BaseScreenPageItem {
  /** 屏显名称，来自 getImageName。 */
  id: string
  /** 屏显物理槽位，范围由 manifest 的 maxScreenSlots 决定。 */
  slot: number
  /** 轮播停留时间，单位秒。 */
  delay: number
  /** 轮播顺位，从 1 开始。 */
  order: number
  /** 是否参与轮播。 */
  show: boolean
}

/** 底座屏幕硬件规格位图。 */
export interface BaseScreenSpecs {
  /** 固件返回的原始规格位图。 */
  value: number
  /** 是否有 LCD/屏幕能力。 */
  lcd: boolean
  /** 是否支持 433M 相关硬件位。 */
  radio433M: boolean
  /** 是否支持电压/电池检测位。 */
  vbat: boolean
}

/** 通过底座透传 EEPROM 读取到的子鼠标标识。 */
export interface BaseConnectedMouse {
  /** 子鼠标 VID，读取失败或未配对时为空。 */
  vendorId?: number
  /** 子鼠标 PID，读取失败或未配对时为空。 */
  productId?: number
}

/** 子鼠标当前连接方式。 */
export type BaseChildConnectType = 'wirelessReceiver' | 'bluetooth' | 'usb' | 'unknown'

/** 子鼠标电池状态。 */
export type BaseBatteryStatus = 'invalid' | 'valid' | 'charging'

/** 底座主动上报的子鼠标状态。 */
export interface BaseChildReport {
  /** 连接方式和传感器模式。 */
  mode: {
    connectType: BaseChildConnectType
    sensorTypeModel: number
  }
  /** DPI 档位和 X/Y DPI 值。 */
  dpi: {
    gear: number
    dpiX: number
    dpiY: number
  }
  /** 电量百分比和充电状态。 */
  battery: {
    battery: number
    status: BaseBatteryStatus
  }
  /** 子鼠标背光模式，保持固件原始值。 */
  blMode: number
  /** 2.4G 和 USB 回报率。 */
  rpt: {
    rpt24g: number
    rptUsb: number
  }
  /** 当前配置索引。 */
  config: number
  /** 原始 input report，便于调试和协议对照。 */
  raw: Uint8Array
}
